(() => {
  const SELECTORS = {
    section: '[data-gmjr-section]',
    controls: '[data-gmjr-controls]',
    sortMount: '[data-gmjr-sort-mount]',
    writeMount: '[data-gmjr-write-mount]',
    loader: '[data-gmjr-loader]',
    pagination: '[data-gmjr-pagination]',
    junipRoot: '.junip-product-review',
    junipContainer: '.junip-product-review-container',
    summary: '.junip-product-review-summary-wrapper',
    sortSelect: '#junipReviewSort, select[aria-label*="Sort"]',
    writeButton: '.junip-write-review-cta',
    averageContainer: '.junip-product-review-average-container',
    count: '.junip-product-review-count',
    bar: '.junip-product-review-bar-wrapper',
    reviewsContainer: '.junip-reviews-container',
    reviewCard: '.junip-review-list-item-container',
    seeMore: '.junip-see-more',
  };

  const SORT_LABELS = {
    mostRecent: 'Sort by newest review',
    oldest: 'Sort by oldest review',
    highestRated: 'Sort by highest rating',
    lowestRated: 'Sort by lowest rating',
    mostHelpful: 'Sort by most helpful',
  };

  const MODAL_VARIABLES = [
    'bg',
    'text',
    'muted',
    'accent',
    'accent-dark',
    'star',
    'panel',
    'card-border',
    'card-shadow',
  ];

  class GoodMoodJunipReviews {
    constructor(section) {
      this.section = section;
      this.controls = section.querySelector(SELECTORS.controls);
      this.sortMount = section.querySelector(SELECTORS.sortMount);
      this.writeMount = section.querySelector(SELECTORS.writeMount);
      this.loader = section.querySelector(SELECTORS.loader);
      this.pagination = section.querySelector(SELECTORS.pagination);
      this.currentPage = 1;
      this.totalReviews = 0;
      this.lastPageSize = this.getPageSize();
      this.loadingPage = false;
      this.paginationSignature = '';
      this.frame = 0;

      this.scheduleSync = this.scheduleSync.bind(this);
      this.sync = this.sync.bind(this);
      this.handleResize = this.handleResize.bind(this);

      this.activateModalSkin();
      this.bindEvents();
      this.observe();
      this.scheduleSync();
    }

    bindEvents() {
      this.section.addEventListener('change', (event) => {
        const select = event.target.closest(SELECTORS.sortSelect);
        if (!select) return;

        this.currentPage = 1;
        this.loadingPage = false;
        window.setTimeout(() => {
          this.updateSortLabel(select);
          this.scheduleSync();
        }, 0);
      });

      this.section.addEventListener('click', (event) => {
        const pageButton = event.target.closest('[data-gmjr-page]');
        if (pageButton && !pageButton.disabled) {
          this.goToPage(Number(pageButton.dataset.gmjrPage) || 1);
          return;
        }

        if (event.target.closest(SELECTORS.bar)) {
          this.currentPage = 1;
          window.setTimeout(this.scheduleSync, 0);
        }
      });

      window.addEventListener('resize', this.handleResize);
    }

    observe() {
      if (!('MutationObserver' in window)) return;
      this.observer = new MutationObserver(this.scheduleSync);
      this.observer.observe(this.section, {
        childList: true,
        subtree: true,
      });
    }

    handleResize() {
      const pageSize = this.getPageSize();
      if (pageSize === this.lastPageSize) return;

      this.lastPageSize = pageSize;
      this.currentPage = 1;
      this.paginationSignature = '';
      this.scheduleSync();
    }

    scheduleSync() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        this.sync();
      });
    }

    sync() {
      this.syncLoadingState();
      this.placeControls();
      this.syncSummary();
      this.syncBars();
      this.syncReviewCards();
      this.renderReviews();
    }

    activateModalSkin() {
      document.documentElement.classList.add('gmjr-junip-skin-active');
      const sectionStyles = window.getComputedStyle(this.section);

      MODAL_VARIABLES.forEach((name) => {
        const value = sectionStyles.getPropertyValue(`--gmjr-${name}`).trim();
        if (value) document.documentElement.style.setProperty(`--gmjr-modal-${name}`, value);
      });
    }

    getJunipRoot() {
      return this.section.querySelector(SELECTORS.junipRoot);
    }

    getJunipContainer() {
      return this.section.querySelector(SELECTORS.junipContainer);
    }

    getPageSize() {
      const mobile = Number(this.section.dataset.gmjrPageSizeMobile) || 3;
      const desktop = Number(this.section.dataset.gmjrPageSizeDesktop) || 4;
      return window.matchMedia('(max-width: 989px)').matches ? mobile : desktop;
    }

    syncLoadingState() {
      if (!this.loader) return;

      const root = this.getJunipRoot();
      const hasPlaceholder = Boolean(this.section.querySelector('.gmjr__placeholder'));
      const initialized = root?.dataset.initialized === 'true';
      const rendered = Boolean(this.getJunipContainer());
      const empty = root?.classList.contains('junip-product-review-empty');
      const loading = !hasPlaceholder && !(rendered || (initialized && empty));

      this.loader.hidden = !loading;
      this.section.classList.toggle('gmjr--loading', loading);
      this.section.classList.toggle('gmjr--ready', !loading && rendered);
    }

    placeControls() {
      if (!this.controls) return;

      const junipContainer = this.getJunipContainer();
      if (junipContainer && this.controls.parentNode !== junipContainer) {
        const summary = junipContainer.querySelector(SELECTORS.summary);
        junipContainer.insertBefore(this.controls, summary || junipContainer.firstChild);
      }

      const sortSelect = this.section.querySelector(SELECTORS.sortSelect);
      const sortControl = sortSelect?.closest('.junip-select');
      if (sortControl && this.sortMount && sortControl.parentNode !== this.sortMount) {
        sortControl.classList.add('gmjr__sort-control');
        this.sortMount.appendChild(sortControl);
        this.updateSortLabel(sortSelect);
      }

      const writeButton = this.section.querySelector(SELECTORS.writeButton);
      if (writeButton && this.writeMount && writeButton.parentNode !== this.writeMount) {
        writeButton.classList.add('gmjr__write-control');
        const writeLabel = this.section.dataset.gmjrWriteReviewLabel || writeButton.textContent;
        if (writeButton.textContent !== writeLabel) writeButton.textContent = writeLabel;
        this.writeMount.appendChild(writeButton);
      }

      const hasControls = Boolean(this.sortMount?.children.length || this.writeMount?.children.length);
      this.controls.hidden = !hasControls;
    }

    updateSortLabel(select) {
      if (!select) return;

      const sortControl = select.closest('.junip-select');
      const label = sortControl?.querySelector('.junip-select-label');
      if (!label) return;

      const selectedOption = select.options[select.selectedIndex];
      const selectedText = selectedOption?.textContent?.trim() || '';
      const labelText = SORT_LABELS[select.value]
        || (selectedText ? `Sort by ${selectedText.toLowerCase()}` : SORT_LABELS.mostRecent);

      const copy = document.createElement('span');
      copy.dataset.gmjrSortCopy = '';
      copy.textContent = labelText;

      const junipSelectedValue = document.createElement('span');
      junipSelectedValue.className = 'junip-selected-value';
      junipSelectedValue.hidden = true;
      junipSelectedValue.setAttribute('aria-hidden', 'true');
      junipSelectedValue.textContent = selectedText;

      label.textContent = '';
      label.append(copy, junipSelectedValue);
    }

    syncSummary() {
      const averageContainer = this.section.querySelector(SELECTORS.averageContainer);
      if (averageContainer) {
        averageContainer.dataset.gmjrRatingTitle = this.section.dataset.gmjrRatingTitle || 'Reviews';
      }

      const count = this.section.querySelector(SELECTORS.count);
      if (!count) return;

      const countNumber = this.extractNumber(count.textContent);
      if (!countNumber) return;

      this.totalReviews = Number(countNumber);
      const countLabel = `(${countNumber} Reviews)`;
      if (count.textContent !== countLabel) count.textContent = countLabel;
    }

    syncBars() {
      this.section.querySelectorAll(SELECTORS.bar).forEach((bar) => {
        const parsed = this.parseBarLabel(bar.getAttribute('aria-label') || '');
        if (!parsed) return;

        const rating = bar.querySelector('.junip-product-review-bar-rating');
        const ratingLabel = `${parsed.rating} ${Number(parsed.rating) === 1 ? 'star' : 'stars'}`;
        if (rating && rating.textContent !== ratingLabel) rating.textContent = ratingLabel;

        let count = bar.querySelector('.gmjr__bar-count');
        if (!count) {
          count = document.createElement('span');
          count.className = 'gmjr__bar-count';
          count.setAttribute('aria-hidden', 'true');
          bar.appendChild(count);
        }
        if (count.textContent !== parsed.count) count.textContent = parsed.count;
      });
    }

    syncReviewCards() {
      this.getReviewCards().forEach((card, index) => {
        card.classList.add('gmjr-review-card');
        card.classList.toggle('gmjr-review-card--white', index % 4 === 0);

        let meta = this.directChild(card, 'gmjr-review-card__meta');
        if (!meta) {
          meta = document.createElement('div');
          meta.className = 'gmjr-review-card__meta';
          card.insertBefore(meta, card.firstChild);
        }

        const reviewItem = this.directChild(card, 'junip-review-list-item');
        const customer = this.directChild(card, 'junip-review-customer')
          || this.directChild(meta, 'junip-review-customer');
        const reviewTop = reviewItem
          ? this.directChild(reviewItem, 'junip-flex-wrapper')
          : this.directChild(meta, 'junip-flex-wrapper');

        if (reviewTop && reviewTop.parentNode !== meta) meta.appendChild(reviewTop);
        if (customer && customer.parentNode !== meta) meta.appendChild(customer);
      });
    }

    renderReviews() {
      const cards = this.getReviewCards();
      if (!cards.length) {
        this.hidePagination();
        return;
      }

      const pageSize = this.getPageSize();
      const totalReviews = Math.max(this.getActiveReviewTotal(), cards.length);
      const totalPages = Math.max(1, Math.ceil(totalReviews / pageSize));
      this.currentPage = Math.min(Math.max(this.currentPage, 1), totalPages);

      const start = (this.currentPage - 1) * pageSize;
      const end = start + pageSize;
      cards.forEach((card, index) => {
        card.classList.toggle('gmjr-review-card--hidden', index < start || index >= end);
      });

      this.renderPagination(totalPages, cards.length);
    }

    renderPagination(totalPages, loadedCount) {
      if (!this.pagination || totalPages <= 1) {
        this.hidePagination();
        return;
      }

      const visibleWindow = this.getVisiblePageWindow(totalPages);
      const signature = [
        totalPages,
        loadedCount,
        this.currentPage,
        visibleWindow.start,
        visibleWindow.end,
        this.loadingPage,
      ].join(':');

      this.pagination.hidden = false;
      this.pagination.setAttribute('aria-busy', String(this.loadingPage));
      if (signature === this.paginationSignature) return;
      this.paginationSignature = signature;

      const buttons = [];
      buttons.push(this.paginationButton(
        Math.max(1, this.currentPage - 1),
        '',
        'gmjr__page--arrow gmjr__page--prev',
        this.currentPage === 1 || this.loadingPage,
        'Previous reviews page',
      ));

      for (let page = visibleWindow.start; page <= visibleWindow.end; page += 1) {
        buttons.push(this.paginationButton(
          page,
          String(page),
          this.currentPage === page ? 'is-active' : '',
          this.loadingPage,
          `Reviews page ${page}`,
        ));
      }

      buttons.push(this.paginationButton(
        Math.min(totalPages, this.currentPage + 1),
        '',
        'gmjr__page--arrow gmjr__page--next',
        this.currentPage === totalPages || this.loadingPage,
        'Next reviews page',
      ));

      this.pagination.innerHTML = buttons.join('');
    }

    hidePagination() {
      if (!this.pagination) return;
      this.pagination.hidden = true;
      this.pagination.innerHTML = '';
      this.paginationSignature = '';
    }

    async goToPage(page) {
      if (this.loadingPage || page === this.currentPage) return;

      const totalPages = Math.max(1, Math.ceil(this.getActiveReviewTotal() / this.getPageSize()));
      const targetPage = Math.min(Math.max(page, 1), totalPages);
      const requiredCount = Math.min(this.getActiveReviewTotal(), targetPage * this.getPageSize());

      if (this.getReviewCards().length < requiredCount) {
        await this.loadReviewsUntil(requiredCount);
      }

      if (this.getReviewCards().length < Math.min(requiredCount, (targetPage - 1) * this.getPageSize() + 1)) {
        return;
      }

      this.currentPage = targetPage;
      this.renderReviews();
      this.section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    async loadReviewsUntil(requiredCount) {
      this.loadingPage = true;
      this.section.classList.add('gmjr--page-loading');
      this.paginationSignature = '';
      this.renderReviews();

      try {
        while (this.getReviewCards().length < requiredCount) {
          const seeMore = this.section.querySelector(SELECTORS.seeMore);
          if (!seeMore || seeMore.disabled) break;

          const previousCount = this.getReviewCards().length;
          seeMore.click();
          const loaded = await this.waitForMoreReviews(previousCount);
          if (!loaded) break;
        }
      } finally {
        this.loadingPage = false;
        this.section.classList.remove('gmjr--page-loading');
        this.paginationSignature = '';
        this.sync();
      }
    }

    waitForMoreReviews(previousCount) {
      return new Promise((resolve) => {
        const startedAt = Date.now();
        const check = () => {
          const currentCount = this.getReviewCards().length;
          if (currentCount > previousCount) {
            resolve(true);
            return;
          }

          if (Date.now() - startedAt > 12000) {
            resolve(false);
            return;
          }

          window.setTimeout(check, 100);
        };
        check();
      });
    }

    getActiveReviewTotal() {
      const selectedBar = Array.from(this.section.querySelectorAll(SELECTORS.bar)).find((bar) => (
        bar.getAttribute('aria-pressed') === 'true' || bar.classList.contains('junip-selected')
      ));
      const selectedCount = selectedBar
        ? this.parseBarLabel(selectedBar.getAttribute('aria-label') || '')?.count
        : null;

      return Number(selectedCount) || this.totalReviews || this.getReviewCards().length;
    }

    getReviewCards() {
      return Array.from(this.section.querySelectorAll(SELECTORS.reviewCard));
    }

    getVisiblePageWindow(totalPages) {
      const maxVisible = Math.min(5, totalPages);
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = start + maxVisible - 1;

      if (end > totalPages) {
        end = totalPages;
        start = Math.max(1, end - maxVisible + 1);
      }

      return { start, end };
    }

    paginationButton(page, label, className, disabled, ariaLabel) {
      const active = className.includes('is-active') ? ' aria-current="page"' : '';
      const disabledAttribute = disabled ? ' disabled' : '';
      return `<button class="gmjr__page ${className}" type="button" data-gmjr-page="${page}" aria-label="${this.escapeAttribute(ariaLabel)}"${active}${disabledAttribute}>${label}</button>`;
    }

    directChild(parent, className) {
      if (!parent) return null;
      return Array.from(parent.children).find((child) => child.classList.contains(className)) || null;
    }

    extractNumber(value) {
      const match = String(value || '').replace(/,/g, '').match(/\d+/);
      return match ? match[0] : '';
    }

    parseBarLabel(label) {
      const match = label.replace(/,/g, '').match(/(\d+)\s+stars?\s+(\d+)\s+reviews?/i);
      if (!match) return null;
      return {
        rating: match[1],
        count: match[2],
      };
    }

    escapeAttribute(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  const init = (root = document) => {
    root.querySelectorAll(SELECTORS.section).forEach((section) => {
      if (section.gmjrInstance) {
        section.gmjrInstance.scheduleSync();
        return;
      }
      section.gmjrInstance = new GoodMoodJunipReviews(section);
    });
  };

  const cleanupModalSkin = () => {
    window.setTimeout(() => {
      if (document.querySelector(SELECTORS.section)) return;
      document.documentElement.classList.remove('gmjr-junip-skin-active');
      MODAL_VARIABLES.forEach((name) => {
        document.documentElement.style.removeProperty(`--gmjr-modal-${name}`);
      });
    }, 0);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', cleanupModalSkin);
})();
